const DEFAULT_LANGUAGE = 'en-US';
const VALID_PRIVACY_MODES = new Set(['require-local', 'prefer-local', 'allow-remote']);
const MAX_SESSION_MS_LIMIT = 30 * 60 * 1000;
const MAX_TRANSCRIPT_CHARS_LIMIT = 250000;
const MAX_RESULT_CHARS = 4000;
function cleanText(value, maxLength = MAX_RESULT_CHARS) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
}
function clampInteger(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(number)));
}
function safeConfidence(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.max(0, Math.min(1, number));
}
function getRecognitionConstructor(env = globalThis) {
  if (!env) return null;
  return env.SpeechRecognition || env.webkitSpeechRecognition || null;
}
export function getSpeechRecognitionSupport(env = globalThis) {
  const Recognition = getRecognitionConstructor(env);
  if (!Recognition) {
    return {
      supported: false,
      constructorName: '',
      canCheckLocalAvailability: false,
      canInstallLocalLanguage: false,
      reason: 'speech-recognition-unavailable',
    };
  }
  return {
    supported: true,
    constructorName: env.SpeechRecognition ? 'SpeechRecognition' : 'webkitSpeechRecognition',
    canCheckLocalAvailability: typeof Recognition.available === 'function',
    canInstallLocalLanguage: typeof Recognition.install === 'function',
    reason: '',
  };
}
function normalizeOptions(options = {}) {
  const privacyMode = VALID_PRIVACY_MODES.has(options.privacyMode)
    ? options.privacyMode
    : 'require-local';
  return {
    language: cleanText(options.language || DEFAULT_LANGUAGE, 40) || DEFAULT_LANGUAGE,
    privacyMode,
    allowRemoteFallback: options.allowRemoteFallback === true,
    interimResults: options.interimResults !== false,
    continuous: options.continuous === true,
    maxAlternatives: clampInteger(options.maxAlternatives, 1, 1, 5),
    maxSessionMs: clampInteger(options.maxSessionMs, 5 * 60 * 1000, 1000, MAX_SESSION_MS_LIMIT),
    maxTranscriptChars: clampInteger(options.maxTranscriptChars, 50000, 1000, MAX_TRANSCRIPT_CHARS_LIMIT),
    requireBrowserUserActivation: options.requireBrowserUserActivation !== false,
    quality: options.quality === 'command' ? 'command' : 'dictation',
  };
}
function localAvailabilityOptions(config) {
  return {
    langs: [config.language],
    processLocally: true,
    quality: config.quality,
  };
}
function hasBrowserUserActivation(env) {
  const activation = env?.navigator?.userActivation;
  if (!activation || typeof activation.isActive !== 'boolean') return null;
  return activation.isActive;
}
function resultAlternatives(result, maxAlternatives) {
  const alternatives = [];
  const length = Math.min(Number(result?.length) || 0, maxAlternatives);
  for (let index = 0; index < length; index += 1) {
    const candidate = result[index];
    const transcript = cleanText(candidate?.transcript);
    if (!transcript) continue;
    alternatives.push({
      transcript,
      confidence: safeConfidence(candidate?.confidence),
    });
  }
  return alternatives;
}
function appendBounded(base, text, limit) {
  const cleaned = cleanText(text, limit);
  if (!cleaned) return base;
  const joined = base ? base + ' ' + cleaned : cleaned;
  if (joined.length <= limit) return joined;
  return joined.slice(joined.length - limit).replace(/^\S*\s?/, '').trimStart();
}
function createStateSnapshot(state) {
  return Object.freeze({
    status: state.status,
    sessionId: state.sessionId,
    privacyRoute: state.privacyRoute,
    localAvailability: state.localAvailability,
    startedAt: state.startedAt,
    endedAt: state.endedAt,
    finalTranscript: state.finalTranscript,
    interimTranscript: state.interimTranscript,
    finalResultCount: state.finalResultCount,
    lastError: state.lastError ? { ...state.lastError } : null,
  });
}
export function createConsentGatedSpeechRecognitionSession(options = {}) {
  const config = normalizeOptions(options);
  const env = options.env || globalThis;
  const Recognition = options.recognitionConstructor || getRecognitionConstructor(env);
  const now = typeof options.now === 'function' ? options.now : () => Date.now();
  const randomId = typeof options.randomId === 'function'
    ? options.randomId
    : () => Math.random().toString(36).slice(2, 10);
  const setTimer = typeof options.setTimer === 'function' ? options.setTimer : (fn, ms) => setTimeout(fn, ms);
  const clearTimer = typeof options.clearTimer === 'function' ? options.clearTimer : (id) => clearTimeout(id);
  const callbacks = {
    onStateChange: typeof options.onStateChange === 'function' ? options.onStateChange : () => {},
    onResult: typeof options.onResult === 'function' ? options.onResult : () => {},
    onError: typeof options.onError === 'function' ? options.onError : () => {},
  };
  const state = {
    status: 'idle',
    sessionId: '',
    privacyRoute: 'none',
    localAvailability: 'unknown',
    startedAt: '',
    endedAt: '',
    finalTranscript: '',
    interimTranscript: '',
    finalResultCount: 0,
    lastError: null,
  };
  let recognition = null;
  let timerId = null;
  let disposed = false;
  let stopReason = '';
  function emitState() {
    callbacks.onStateChange(createStateSnapshot(state));
  }
  function setStatus(status) {
    state.status = status;
    emitState();
  }
  function setError(code, message, detail = '') {
    const error = {
      code: cleanText(code, 80) || 'speech-recognition-error',
      message: cleanText(message, 300) || 'Speech recognition failed.',
      detail: cleanText(detail, 300),
      at: new Date(now()).toISOString(),
    };
    state.lastError = error;
    callbacks.onError({ ...error });
    emitState();
    return error;
  }
  function ensureUsable() {
    if (disposed) throw new Error('Speech recognition session is disposed.');
    if (!Recognition) throw new Error('Speech recognition is not available in this environment.');
  }
  async function checkLocalAvailability() {
    ensureUsable();
    if (typeof Recognition.available !== 'function') {
      state.localAvailability = 'api-unavailable';
      emitState();
      return state.localAvailability;
    }
    try {
      const value = await Recognition.available(localAvailabilityOptions(config));
      const normalized = cleanText(value, 40) || 'unknown';
      state.localAvailability = normalized;
      emitState();
      return normalized;
    } catch (error) {
      state.localAvailability = 'check-failed';
      setError('local-availability-check-failed', 'Could not check on-device speech recognition availability.', error?.message);
      return state.localAvailability;
    }
  }
  async function installLocalLanguage({ userInitiated = false } = {}) {
    ensureUsable();
    if (!userInitiated) {
      throw new Error('Installing a speech language pack requires an explicit user action.');
    }
    if (typeof Recognition.install !== 'function') {
      throw new Error('This browser cannot install an on-device speech language pack through the Web Speech API.');
    }
    try {
      setStatus('installing-language');
      const installed = await Recognition.install(localAvailabilityOptions(config));
      state.localAvailability = installed ? 'available' : 'install-failed';
      setStatus('idle');
      return installed === true;
    } catch (error) {
      state.localAvailability = 'install-failed';
      setStatus('idle');
      setError('local-language-install-failed', 'The on-device speech language pack could not be installed.', error?.message);
      return false;
    }
  }
  async function resolvePrivacyRoute() {
    if (config.privacyMode === 'allow-remote') {
      state.privacyRoute = 'browser-managed';
      return state.privacyRoute;
    }
    const availability = await checkLocalAvailability();
    if (availability === 'available') {
      state.privacyRoute = 'on-device-required';
      return state.privacyRoute;
    }
    if (config.privacyMode === 'prefer-local' && config.allowRemoteFallback) {
      state.privacyRoute = 'browser-managed-fallback';
      return state.privacyRoute;
    }
    state.privacyRoute = 'blocked';
    if (availability === 'downloadable' || availability === 'downloading') {
      throw new Error('On-device speech recognition requires a language pack. Call installLocalLanguage from an explicit user action first.');
    }
    if (availability === 'api-unavailable') {
      throw new Error('This browser cannot guarantee on-device speech recognition. Remote recognition was not explicitly allowed.');
    }
    throw new Error('On-device speech recognition is not available for the selected language and remote recognition was not explicitly allowed.');
  }
  function clearSessionTimer() {
    if (timerId !== null) {
      clearTimer(timerId);
      timerId = null;
    }
  }
  function detachRecognition() {
    if (!recognition) return;
    recognition.onstart = null;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition = null;
  }
  function finish(status = 'ended') {
    clearSessionTimer();
    state.endedAt = new Date(now()).toISOString();
    state.interimTranscript = '';
    detachRecognition();
    setStatus(status);
  }
  function handleResult(event) {
    const from = Math.max(0, Number(event?.resultIndex) || 0);
    const results = event?.results || [];
    let interim = '';
    const emitted = [];
    for (let index = from; index < results.length; index += 1) {
      const result = results[index];
      const alternatives = resultAlternatives(result, config.maxAlternatives);
      if (!alternatives.length) continue;
      const item = Object.freeze({
        isFinal: result?.isFinal === true,
        transcript: alternatives[0].transcript,
        confidence: alternatives[0].confidence,
        alternatives: Object.freeze(alternatives),
        resultIndex: index,
        at: new Date(now()).toISOString(),
      });
      emitted.push(item);
      if (item.isFinal) {
        state.finalTranscript = appendBounded(state.finalTranscript, item.transcript, config.maxTranscriptChars);
        state.finalResultCount += 1;
      } else {
        interim = appendBounded(interim, item.transcript, config.maxTranscriptChars);
      }
    }
    state.interimTranscript = interim;
    for (const item of emitted) callbacks.onResult(item, createStateSnapshot(state));
    emitState();
  }
  function handleRecognitionError(event) {
    const code = cleanText(event?.error, 80) || 'unknown';
    const message = cleanText(event?.message, 300) || 'Speech recognition reported an error.';
    setError(code, message);
  }
  function buildRecognition() {
    const instance = new Recognition();
    instance.lang = config.language;
    instance.continuous = config.continuous;
    instance.interimResults = config.interimResults;
    instance.maxAlternatives = config.maxAlternatives;
    if ('processLocally' in instance) {
      instance.processLocally = state.privacyRoute === 'on-device-required';
    } else if (state.privacyRoute === 'on-device-required') {
      throw new Error('This browser cannot enforce on-device speech recognition for this session.');
    }
    instance.onstart = () => {
      state.startedAt = new Date(now()).toISOString();
      setStatus('listening');
    };
    instance.onresult = handleResult;
    instance.onerror = handleRecognitionError;
    instance.onend = () => {
      const reason = stopReason;
      stopReason = '';
      if (disposed || state.status === 'disposed') return;
      finish(reason === 'aborted' ? 'aborted' : reason === 'stopped' ? 'ended' : 'ended');
    };
    return instance;
  }
  async function start({ consentGranted = false, userInitiated = false } = {}) {
    ensureUsable();
    if (!consentGranted) throw new Error('Speech recognition requires explicit consent for this session.');
    if (!userInitiated) throw new Error('Speech recognition must be started by an explicit user action.');
    if (!['idle', 'ended', 'aborted', 'error'].includes(state.status)) {
      throw new Error('Speech recognition is already active or preparing.');
    }
    const browserActivation = hasBrowserUserActivation(env);
    if (config.requireBrowserUserActivation && browserActivation === false) {
      throw new Error('The browser reports that there is no active user gesture for speech recognition.');
    }
    state.lastError = null;
    state.endedAt = '';
    state.sessionId = 'speech-' + now() + '-' + randomId();
    setStatus('preparing');
    try {
      await resolvePrivacyRoute();
      recognition = buildRecognition();
      stopReason = '';
      recognition.start();
      timerId = setTimer(() => {
        if (recognition && (state.status === 'listening' || state.status === 'preparing')) {
          stopReason = 'stopped';
          try {
            recognition.stop();
          } catch (error) {
            setError('auto-stop-failed', 'Speech recognition reached the session limit but could not stop cleanly.', error?.message);
            finish('error');
          }
        }
      }, config.maxSessionMs);
      return state.sessionId;
    } catch (error) {
      clearSessionTimer();
      detachRecognition();
      state.privacyRoute = state.privacyRoute || 'blocked';
      setStatus('error');
      setError('start-failed', error?.message || 'Speech recognition could not start.');
      throw error;
    }
  }
  function stop({ userInitiated = false } = {}) {
    if (!userInitiated) throw new Error('Stopping speech recognition requires an explicit user action.');
    if (!recognition || !['preparing', 'listening'].includes(state.status)) return false;
    stopReason = 'stopped';
    setStatus('stopping');
    try {
      recognition.stop();
      return true;
    } catch (error) {
      setError('stop-failed', 'Speech recognition could not stop cleanly.', error?.message);
      finish('error');
      return false;
    }
  }
  function abort({ userInitiated = false } = {}) {
    if (!userInitiated) throw new Error('Aborting speech recognition requires an explicit user action.');
    if (!recognition || !['preparing', 'listening', 'stopping'].includes(state.status)) return false;
    stopReason = 'aborted';
    try {
      recognition.abort();
      return true;
    } catch (error) {
      setError('abort-failed', 'Speech recognition could not abort cleanly.', error?.message);
      finish('error');
      return false;
    }
  }
  function resetTranscript({ userInitiated = false } = {}) {
    if (!userInitiated) throw new Error('Clearing a transcript requires an explicit user action.');
    if (['preparing', 'listening', 'stopping'].includes(state.status)) {
      throw new Error('Stop speech recognition before clearing the transcript.');
    }
    state.finalTranscript = '';
    state.interimTranscript = '';
    state.finalResultCount = 0;
    emitState();
  }
  function getState() {
    return createStateSnapshot(state);
  }
  function getConfig() {
    return Object.freeze({ ...config });
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    clearSessionTimer();
    if (recognition) {
      try { recognition.abort(); } catch {}
    }
    detachRecognition();
    state.status = 'disposed';
    state.endedAt = new Date(now()).toISOString();
    callbacks.onStateChange(createStateSnapshot(state));
  }
  return Object.freeze({
    start,
    stop,
    abort,
    dispose,
    resetTranscript,
    checkLocalAvailability,
    installLocalLanguage,
    getState,
    getConfig,
  });
}
export function getSpeechRecognitionSafetyLimits() {
  return Object.freeze({
    maxSessionMs: MAX_SESSION_MS_LIMIT,
    maxTranscriptChars: MAX_TRANSCRIPT_CHARS_LIMIT,
    maxResultChars: MAX_RESULT_CHARS,
    defaultPrivacyMode: 'require-local',
    autoRestart: false,
    audioStorage: false,
  });
}
